"use client";

import React, { useState, useEffect } from "react";
import { Check, ArrowRight, ShieldCheck, Sparkles, Factory, Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const categories = [{
  id: "tailings-management",
  name: "Tailings Management",
  icon: ShieldCheck,
  description: "Strategic oversight and lifecycle stewardship for impoundment facilities. DSR, annual inspections, audits and assurance to safe and efficient operations..",
  services: [{
    title: "Engineer of Record (EOR) Services",
    description: "Comprehensive accountability for the design, construction, and operational safety of Tailings Storage Facilities (TSFs).",
    bullets: ["Annual performance and dam safety inspections", "Governance and stewardship framework development", "Risk management and FMEA facilitation", "Long-term closure and reclamation planning"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/tailings_dam_oversight.png"
  }, {
    title: "Water Management & Balance",
    description: "Advanced hydraulic modeling to optimize water recovery and ensure containment integrity during extreme events.",
    bullets: ["Site-wide water balance and reduction strategies", "Flood routing and spillway capacity analysis", "Seepage collection and monitoring systems", "Process water recycling optimization"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/water_management_system.png"
  }, {
    title: "Operational Support",
    description: "On-site technical assistance to ensure deposition and containment practices align with design intent.",
    bullets: ["Deposition modeling and beaching analysis", "Instrumentation data interpretation (Piezometers, Inclinometers)", "Operator training and manual development", "Emergency Preparedness & Response Plans (EPRP)"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/tailings_operations_support.png"
  }]
}, {
  id: "geotechnical-engineering",
  name: "Geotechnical Projects",
  icon: Factory,
  description: "Technical design, construction and peer review for critical earthwork infrastructure.",
  services: [{
    title: "Site Investigation & Characterization",
    description: "Subsurface exploration and laboratory testing to define geological parameters for foundation and embankment design.",
    bullets: ["CPTu and geotechnical drilling management", "In-situ testing and shear wave velocity profiling", "Advanced laboratory testing program design", "Geological and hydrogeological modeling"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/geotechnical_drilling_site.png"
  }, {
    title: "Stability & Deformation Analysis",
    description: "Rigorous assessment of structural integrity using state-of-the-art numerical modeling and limit equilibrium methods.",
    bullets: ["2D and 3D limit equilibrium stability analysis", "Finite element deformation and stress modeling", "Liquefaction susceptibility assessments", "Seismic hazard and site response analysis"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/stability_analysis_model.png"
  }, {
    title: "Construction Support (CQA)",
    description: "Quality assurance and technical oversight during the construction of earthwork and geosynthetic structures.",
    bullets: ["Construction Quality Assurance (CQA) monitoring", "Earthwork and liner installation oversight", "Field density and moisture content verification", "As-built documentation and certification"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/construction_qa_oversight.png"
  }]
}, {
  id: "compliance-oversight",
  name: "Compliance & Oversight",
  icon: Leaf,
  description: "Ensuring adherence to international standards and environmental best practices.",
  services: [{
    title: "GISTM & ICMM Implementation",
    description: "Assisting operators in aligning facilities with the Global Industry Standard on Tailings Management and ICMM requirements.",
    bullets: ["Gap analysis and implementation roadmaps", "Social and environmental impact integration", "Public disclosure and transparency reporting", "Multi-disciplinary conformance auditing"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/compliance_audit_review.png"
  }, {
    title: "Independent Review (IRTB)",
    description: "Facilitating Independent Review Boards to provide high-level technical scrutiny of project risks and designs.",
    bullets: ["Panel selection and meeting facilitation", "Technical memorandum and report synthesis", "Peer review of complex design methodologies", "Third-party safety and governance audits"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618705/img/irtb_review_session.png"
  }]
}];
export default function ServiceDirectory() {
  const [activeCategory, setActiveCategory] = useState<string>(categories[0].id);
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const category of categories) {
        const element = document.getElementById(category.id);
        if (element) {
          const {
            offsetTop,
            offsetHeight
          } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveCategory(category.id);
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="31091" id="our-specialized-expertise" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-foreground mb-6 tracking-tighter uppercase">
            Specialized Expertise
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-sans">ATG Engineering and Consulting provides world-class technical oversight in phosphogypsum, tailings management and geotechnical engineering. Select a discipline to view our technical methodologies and compliance standards.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 xl:gap-20">
          {}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-32 space-y-8">
              <div className="space-y-2">
                <h3 className="text-xs font-mono font-bold text-secondary uppercase tracking-widest px-4 mb-4">
                  Disciplines
                </h3>
                <nav className="flex flex-col space-y-1">
                  {categories.map(category => <Link key={category.id} to={`#${category.id}`} className={cn("group flex items-center gap-3 px-4 py-3 rounded-none text-sm font-bold transition-all duration-300 border-l-2", activeCategory === category.id ? "bg-primary/10 text-primary border-primary shadow-sm" : "text-muted-foreground border-transparent hover:bg-accent hover:text-foreground")}>
                      <category.icon className={cn("size-4 transition-colors", activeCategory === category.id ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} />
                      {category.name}
                      {activeCategory === category.id && <ArrowRight className="ml-auto size-3.5 animate-pulse" />}
                    </Link>)}
                </nav>
              </div>

              <div className="p-6 rounded-none bg-card border border-border backdrop-blur-sm">
                <h4 className="font-sans font-extrabold text-lg text-foreground mb-2 uppercase tracking-tight">Technical Inquiry?</h4>
                <p className="text-sm text-muted-foreground mb-4 font-sans">
                  We tailor our engineering solutions to specific geological conditions and regulatory environments.
                </p>
                <Button className="w-full rounded-none font-bold uppercase tracking-wider" asChild>
                  <Link to="/ContactPage">Consult an Expert</Link>
                </Button>
              </div>
            </div>
          </aside>

          {}
          <div className="lg:hidden -mx-4 px-4 pb-4 overflow-x-auto flex gap-3 scrollbar-hide sticky top-16 z-20 bg-background/95 backdrop-blur-md py-4 border-b border-border">
            {categories.map(category => <Link key={category.id} to={`#${category.id}`} className={cn("flex items-center gap-2 px-4 py-2 rounded-none text-xs font-bold uppercase tracking-wider whitespace-nowrap border transition-colors", activeCategory === category.id ? "bg-primary text-primary-foreground border-primary" : "bg-card text-card-foreground border-border hover:border-primary/50")}>
                <category.icon className="size-3.5" />
                {category.name}
              </Link>)}
          </div>

          {}
          <div className="flex-1 space-y-24 pb-20">
            {categories.map((category, index) => <div key={category.id} id={category.id} className="scroll-mt-32 group/category" data-index={index}>
                <div className="flex items-center gap-4 mb-8 border-b border-border pb-4">
                  <div className="p-3 rounded-none bg-accent text-primary">
                    <category.icon className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-sans font-extrabold text-foreground uppercase tracking-tight">
                      {category.name}
                    </h3>
                    <p className="text-muted-foreground text-sm md:text-base mt-1 font-sans">
                      {category.description}
                    </p>
                  </div>
                </div>

                <Accordion type="single" collapsible className="w-full space-y-4">
                  {category.services.map((service, sIndex) => <AccordionItem key={sIndex} value={service.title} className="border border-border rounded-none bg-card/50 overflow-hidden shadow-none hover:border-primary/30 transition-all duration-300 data-[state=open]:border-primary/50 data-[state=open]:bg-card">
                      
                      <AccordionContent className="px-0 pb-0">
                        <div className="grid md:grid-cols-2 gap-0">
                          {}
                          

                          {}
                          <div className="relative h-64 md:h-auto min-h-[300px] bg-muted">
                            <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 hover:scale-105" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent md:bg-gradient-to-l" />
                            <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
                              <span className="inline-flex items-center px-3 py-1 rounded-none text-[10px] font-mono font-bold bg-foreground text-background backdrop-blur-sm shadow-sm uppercase tracking-widest">
                                <Leaf className="w-3 h-3 mr-1.5 text-primary" />
                                Engineering Grade
                              </span>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}